import {
  BACKWARDS_B1GY_SUBEXPERIENCES,
  BACKWARDS_BOGO_SUBEXPERIENCE_TAGS,
  BACKWARDS_BOGO_SUBEXPERIENCES,
  DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCES,
  EXPERIENCE_TAGS,
  FORWARDS_B1GY_SUBEXPERIENCES,
  FORWARDS_BOGO_SUBEXPERIENCE_TAGS,
  FORWARDS_BOGO_SUBEXPERIENCES,
  MESSAGE_NUMBER_WORD_MAP,
} from './constants';

export const noop = () => { };

/**
 * @param {string} url
 * @returns {string}
 */
export const sanitizeImageUrl = (url = '') => url.replace(/<SIZE>/g, '400');

export const pipe = (...funcs) => (arg) => funcs.reduce((acc, cur) => cur(acc), arg);

/**
 * @param {Object} obj
 * @param {string} obj.subExperienceTag
 * @returns {boolean}
 */
export const isForwardsBogo = ({ subExperienceTag }) => {
  return FORWARDS_BOGO_SUBEXPERIENCES.includes(subExperienceTag);
};

/**
 * @param {Object} obj
 * @param {string} obj.subExperienceTag
 * @returns {boolean}
 */
export const isBackwardsBogo = ({ subExperienceTag }) => {
  return BACKWARDS_BOGO_SUBEXPERIENCES.includes(subExperienceTag);
};

/**
 * @param {Object} obj
 * @param {string} obj.subExperienceTag
 * @returns {boolean}
 */
export const isDollarThresholdBogo = ({ subExperienceTag }) => {
  return DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCES.includes(subExperienceTag);
};

/**
 * @param {Object} obj
 * @param {string} obj.subExperienceTag
 * @returns {boolean}
 */
export const isForwardsB1gy = ({ subExperienceTag }) => {
  return FORWARDS_B1GY_SUBEXPERIENCES.includes(subExperienceTag);
};

/**
 * @param {Object} obj
 * @param {string} obj.subExperienceTag
 * @returns {boolean}
 */
export const isBackwardsB1gy = ({ subExperienceTag }) => {
  return BACKWARDS_B1GY_SUBEXPERIENCES.includes(subExperienceTag);
};

/**
 * @param {'SRC-1' | 'TGT-1'} itemGroup
 * @param {Object} conditionalPromotion
 * @returns {string[]}
 */
const getItemIdList = (itemGroup, conditionalPromotion) => {
  return conditionalPromotion.eligibilityCriteria?.find((eligibilityCriterion) => {
    return eligibilityCriterion.itemGroup === itemGroup;
  })?.omsSkus?.slice() || [];
};

/**
 * applies business logic to determine the "first" and "second" lists.
 * @param {Object} anchorItem
 * @returns {{ firstListItemIds: string[], secondListItemIds: string[] }}
 */
export const getItemIdLists = (anchorItem) => {
  const conditionalPromotion = anchorItem.pricing.conditionalPromotions[0];
  const anchorItemFilter = (itemId) => (itemId !== anchorItem.itemId);
  let firstListItemIds = getItemIdList('SRC-1', conditionalPromotion).filter(anchorItemFilter);
  let secondListItemIds = getItemIdList('TGT-1', conditionalPromotion).filter(anchorItemFilter);

  switch (conditionalPromotion.subExperienceTag) {
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne:
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff:
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff: {
    firstListItemIds = secondListItemIds;
    secondListItemIds = [];
    break;
  }
  case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne:
  case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff:
  case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff: {
    secondListItemIds = [];
    break;
  }
  default: {
    // all non-simple BOGOs place the anchor at the top
    firstListItemIds = [
      anchorItem.itemId,
      ...firstListItemIds,
    ];
    break;
  }
  }

  return {
    firstListItemIds,
    secondListItemIds,
  };
};

export const getProductImage = (productSeed) => productSeed.media?.image?.url || '';

/**
 *
 * @param {Object} obj
 * @param {boolean | null} [obj.prop]
 * @param {boolean | null} [obj.config]
 * @param {boolean} [obj.defaultValue]
 * @returns {boolean}
 */
export const getIsFeatureEnabled = ({ prop, config, defaultValue = false }) => {
  if (prop === false || config === false) return false;
  if (prop === true || config === true) return true;
  return Boolean(defaultValue);
};

/**
 * @param {Object} obj
 * @param {string | null} obj.experienceTag
 * @param {string | null} obj.subExperienceTag
 * @param {boolean | null} [obj.backwardsBogoEnabled] Backwards BOGO feature switch
 * @param {boolean | null} [obj.bogoDollarThresholdEnabled] BOGO dollar threshold feature switch
 * @param {boolean | null} [obj.bogoEnabled] Overall BOGO feature switch
 * @param {boolean | null} [obj.bmsmEnabled] BMSM feature switch
 * @param {boolean | null} [obj.msbEnabled] MSB feature switch
 * @returns {boolean} Whether the promotion is enabled
 */
export const isPromotionEnabled = ({
  experienceTag,
  subExperienceTag,
  backwardsBogoEnabled,
  bogoDollarThresholdEnabled,
  bogoEnabled,
  bmsmEnabled,
  msbEnabled,
  type
}) => {
  const isCard = type === 'card';
  switch (experienceTag) {
  case EXPERIENCE_TAGS.BOGO: {
    if (isForwardsBogo({ subExperienceTag })) {
      return Boolean(bogoEnabled);
    }

    if (isBackwardsBogo({ subExperienceTag })) {
      return Boolean(bogoEnabled && backwardsBogoEnabled && !isCard);
    }

    if (isDollarThresholdBogo({ subExperienceTag })) {
      return Boolean(bogoEnabled && bogoDollarThresholdEnabled && !isCard);
    }

    if (isForwardsB1gy({ subExperienceTag })) {
      return !isCard;
    }

    return false;
  }
  case EXPERIENCE_TAGS.BMSM:
    return Boolean(bmsmEnabled && !isCard);
  case EXPERIENCE_TAGS.MSB:
    return Boolean(msbEnabled && !isCard);
  default:
    return false;
  }
};

export const toWord = (num) => {
  return MESSAGE_NUMBER_WORD_MAP[num] || num;
};
